import React from 'react';

import { Typography } from '@mui/material';
import _ from 'lodash';
import { createSelector } from 'reselect';

import { editableSections } from 'constants/fees';
import { FeeCalculationType } from 'constants/feeTypes';
import {
  EscrowRequirementType,
  HelocPurposeType,
  LESectionCDType,
} from 'constants/loanApplication';
import {
  currencyOptions,
  ExtraFeesFieldsMapper,
  LoanFeePayerEnum,
  LoanFeePaymentTimeEnum,
} from 'constants/loanFee';
import { PendingChangesActionType } from 'constants/pendingChanges';
import { getConditionalArray, getConditionalObject } from 'utils/data';
import {
  checkIsImpoundCollected,
  getCalculatedImpoundFields,
  getImpoundDetails,
} from 'utils/impounds';
import { checkIsAdditionalLoanFlow, checkIsPurchaseFlow } from 'utils/loanApplication';
import {
  enrichFeeWithFeeTypeFields,
  getSectionEFeesValues,
  getSellerAdjustmentViewLabel,
} from 'utils/loanFees';
import { formatNumberToCurrencyString } from 'utils/number';
import { naturalSortBy } from 'utils/sort';

import { selectFeeTypes, selectSellerAdjustmentTypesOptions } from 'store/system/selectors';

import { selectLoanApplication } from './loanApplication.selectors';
import {
  selectCommonInfoLoanApplication,
  selectIsRefinanceFeesFlow,
} from './loanApplicationCommonInfo.selectors';
import { foldAppraisalSurcharges } from './loanFees.selectors.utils';

const emptyCalculatedSectionTotals = {
  totalAtClosingAmount: 0,
  totalBeforeClosingAmount: 0,
  sellerPaidAtClosingAmount: 0,
  sellerPaidBeforeClosingAmount: 0,
  paidByOthersAmount: 0,
};

const getFormattedModifiedFields = modifiedFields => [
  ...(modifiedFields || []),
  ..._.map(
    ExtraFeesFieldsMapper,
    (group, key) => _.some(group, prop => _.includes(modifiedFields, prop)) && key,
  ).filter(Boolean),
];

export const selectLoanFees = createSelector(selectLoanApplication, ({ loanFees }) => loanFees);

const selectLoanFeesValues = createSelector(selectLoanFees, ({ fees }) => fees);

const selectLoanFeesCureBaseline = createSelector(
  selectLoanFees,
  ({ cureBaseline }) => cureBaseline,
);

export const selectIsSellerPaidFieldsVisible = createSelector(
  selectCommonInfoLoanApplication,
  ({ loanPurpose, helocPurpose = HelocPurposeType.HOME_PURCHASE }) => {
    const isHomePurchase = helocPurpose === HelocPurposeType.HOME_PURCHASE;

    return (
      checkIsPurchaseFlow(loanPurpose) || (checkIsAdditionalLoanFlow(loanPurpose) && isHomePurchase)
    );
  },
);

export const selectExtendedLoanFeesValues = createSelector(
  [selectLoanFeesValues, selectFeeTypes, selectLoanFeesCureBaseline],
  (loanFees, allFees, cureBaseline) =>
    loanFees.reduce((acc, loanFee) => {
      const loanFeeInAcc = acc[loanFee.feeTypeId];
      const isOriginalFeeSetInAcc = loanFeeInAcc && loanFee.isOverride;
      const feeType = allFees[loanFee.feeTypeId];

      const matchedCureBaselineFee = _.find(
        cureBaseline?.fees,
        ({ feeTypeId }) => feeTypeId === loanFee.feeTypeId,
      );

      if (!loanFeeInAcc) {
        acc[loanFee.feeTypeId] = {
          ...loanFee,
          isAdded: loanFee.isOverride,
          defaultAmount: loanFee.amount,
        };
      }

      if (loanFeeInAcc) {
        acc[loanFee.feeTypeId] = {
          ...(isOriginalFeeSetInAcc ? loanFee : loanFeeInAcc),
          isAdded: false,
          defaultAmount: isOriginalFeeSetInAcc ? loanFeeInAcc.amount : loanFee.amount,
        };
      }

      const lastDisclosedAmount = matchedCureBaselineFee?.amount || null;

      acc[loanFee.feeTypeId].lastDisclosedAmount = lastDisclosedAmount;
      acc[loanFee.feeTypeId].borrowerPaidBeforeClosingAmount =
        matchedCureBaselineFee?.borrowerPaidBeforeClosingAmount;

      acc[loanFee.feeTypeId].excludeFromTexasCashOutCure = feeType?.excludeFromTexasCashOutCure;
      acc[loanFee.feeTypeId].cdSection = LESectionCDType[feeType?.leSection];
      acc[loanFee.feeTypeId].isVisible = !feeType?.isSellerPaid;
      acc[loanFee.feeTypeId].name = allFees[loanFee.feeTypeId]?.name;
      acc[loanFee.feeTypeId].cureType = feeType?.cureType;
      acc[loanFee.feeTypeId].isCustomDisabled =
        !feeType?.isEnabled && feeType?.calculationType === FeeCalculationType.CUSTOM;
      acc[loanFee.feeTypeId].countTowardsApr = feeType?.countTowardsApr;

      acc[loanFee.feeTypeId].paymentTime =
        _.isBoolean(acc[loanFee.feeTypeId]?.beforeClosing) && acc[loanFee.feeTypeId]?.beforeClosing
          ? LoanFeePaymentTimeEnum.BEFORE_CLOSING
          : LoanFeePaymentTimeEnum.AT_CLOSING;

      if (!acc[loanFee.feeTypeId].feePayer) {
        acc[loanFee.feeTypeId].feePayer = feeType?.isSellerPaid
          ? LoanFeePayerEnum.SELLER
          : LoanFeePayerEnum.BORROWER;
      }

      return acc;
    }, {}),
);

export const selectLoanFeesSummary = createSelector(selectLoanFees, ({ summary }) => summary);

const selectLoanFeesView = createSelector(selectLoanFees, ({ view }) => view);

export const selectTaxesInsuranceEscrowedLoanFeesView = createSelector(
  selectLoanFeesView,
  ({ taxesInsuranceEscrowed }) => !!taxesInsuranceEscrowed,
);

export const selectApplicationEscrowData = createSelector(selectLoanFeesView, loanFeesView => {
  const { applicationEscrowData } = loanFeesView;
  const { escrowRequirement, specificEscrowInsurancesData } = applicationEscrowData ?? {};
  const { floodInsuranceImpoundId, homeownersInsuranceImpoundId } =
    specificEscrowInsurancesData ?? {};

  const isEscrowNotAllowed = escrowRequirement === EscrowRequirementType.NOT_ALLOWED;

  return {
    escrowRequirement,
    isEscrowNotAllowed,
    floodInsuranceImpoundId,
    homeownersInsuranceImpoundId,
  };
});

export const selectIsEscrowApplicable = createSelector(
  [selectTaxesInsuranceEscrowedLoanFeesView, selectApplicationEscrowData],
  (taxesInsuranceEscrowed, { isEscrowNotAllowed }) => taxesInsuranceEscrowed && !isEscrowNotAllowed,
);

export const selectLoanFeesCashToCloseAmount = createSelector(
  selectLoanFeesView,
  ({ cashToClose }) => cashToClose?.cashToCloseAmount ?? null,
);

export const selectPrepaidDataLoanFeesView = createSelector(
  selectLoanFeesView,
  ({ prepaidInterest, prepaidInterestDays }) => ({
    prepaidInterest: prepaidInterest ?? 0,
    prepaidInterestDays: prepaidInterestDays ?? 0,
  }),
);

export const selectFormattedLoanFeesView = createSelector(
  [selectLoanFeesView, selectLoanFeesSummary, selectIsRefinanceFeesFlow, selectFeeTypes],
  (loanFeesView, summary, isRefinanceFeesFlow, feeTypes) => {
    const {
      aggregateAdjustment,
      lenderCredit,
      sections,
      sectionF,
      sectionG,
      taxesInsuranceEscrowed,
      totalLoanAmount,
      calculatedSections,
      cashToClose,
      closingCostsPayedBeforeClosing,
      modifiedFields,
    } = loanFeesView;

    const sectionK = loanFeesView.sectionK ?? {};
    const sectionL = loanFeesView.sectionL ?? {};

    const { E: sectionE, ...otherSections } = sections || {};
    const sectionEFeesValues = getSectionEFeesValues({ fees: sectionE?.fees, isEdit: false });

    const totalCure = _.get(summary, 'cureSummary.totalCure', 0);
    const isCureHeld = _.get(summary, 'cureSummary.isCureHeld', false);
    const isPricingPremiumHeld = _.get(summary, 'cureSummary.isPricingPremiumHeld', false);
    const heldPricingPremiumAmount = _.get(summary, 'cureSummary.heldPricingPremiumAmount', 0);

    const sectionGAggregateAdjustment = taxesInsuranceEscrowed ? aggregateAdjustment : 0;
    const sectionGItems = naturalSortBy(sectionG || [], 'impoundTypeName').map(impound => {
      const isCollected = checkIsImpoundCollected(impound, taxesInsuranceEscrowed);

      return {
        ...impound,
        name: isCollected ? getImpoundDetails(impound) : impound.impoundTypeName,
        amount: isCollected ? impound.monthsCollected * impound.monthlyAmount : 0,
        modifiedFields: getFormattedModifiedFields(impound.modifiedFields),
      };
    });

    return {
      main: {
        ..._.mapValues(otherSections, s => ({
          ...s,
          totalAmount: s.totalAmountPayedByBorrower,
          items: foldAppraisalSurcharges(
            naturalSortBy(s.fees, 'feeName').map(fee =>
              enrichFeeWithFeeTypeFields({ ...fee, name: fee.feeName }, feeTypes),
            ),
            feeTypes,
          ),
        })),
        E: {
          ...sectionE,
          totalAmount: sectionE?.totalAmountPayedByBorrower,
          groups: [
            {
              isFeeProviderColumnVisible: false,
              recordingFees: sectionEFeesValues.recordingFees.map(fee =>
                enrichFeeWithFeeTypeFields(
                  {
                    ...fee,
                    name: fee.feeName,
                    modifiedFields: getFormattedModifiedFields(fee.modifiedFields),
                  },
                  feeTypes,
                ),
              ),
              items: sectionEFeesValues.recordingFeesTotal,
            },
            {
              items: sectionEFeesValues.transferTaxes.map(fee =>
                enrichFeeWithFeeTypeFields(
                  {
                    ...fee,
                    name: fee.feeName,
                    modifiedFields: getFormattedModifiedFields(fee.modifiedFields),
                  },
                  feeTypes,
                ),
              ),
            },
          ],
        },
        D: {
          isTotalView: true,
          isNonBorrowerPaidFieldsIncluded: true,
          isFeeProviderColumnVisible: false,
          totalAmount: calculatedSections?.D?.totalAmount,
          items: [
            {
              ...(calculatedSections?.D || emptyCalculatedSectionTotals),
              name: 'Loan Costs Subtotals (A + B + C)',
            },
          ],
        },
        F: {
          amountColumnName: 'Total Collected',
          totalAmount: sectionF?.totalCollected,
          items: naturalSortBy(sectionF?.prepaids || [], 'feeName')
            .map(fee =>
              enrichFeeWithFeeTypeFields(
                {
                  ...fee,
                  name: `${fee.feeName} (${fee.monthsCollected} mo.)${
                    fee.feeProviderName ? ` to ${fee.feeProviderName}` : ''
                  }`,
                  amount: fee.totalCollected,
                  modifiedFields: getFormattedModifiedFields(fee.modifiedFields),
                },
                feeTypes,
              ),
            )
            .concat(
              getConditionalArray(sectionF?.prepaidInterestDescription, () => [
                {
                  name: sectionF.prepaidInterestDescription,
                  amount: sectionF.prepaidInterestAmount,
                  modifiedFields: getFormattedModifiedFields(sectionF.modifiedFields),
                },
              ]),
            ),
          isNameAmountSection: true,
        },
        G: {
          amountColumnName: 'Total Collected',
          isNameAmountSection: true,
          totalAmount: _.sumBy(sectionGItems, 'amount') + sectionGAggregateAdjustment,
          items: sectionGItems.concat({
            name: 'Aggregate Adjustment',
            amount: sectionGAggregateAdjustment,
            modifiedFields: getFormattedModifiedFields(modifiedFields),
          }),
        },
        I: {
          isTotalView: true,
          isNonBorrowerPaidFieldsIncluded: true,
          isFeeProviderColumnVisible: false,
          totalAmount: calculatedSections?.I?.totalAmount,
          items: [
            {
              ...(calculatedSections?.I || emptyCalculatedSectionTotals),
              name: 'Other Costs Subtotals (E + F + G + H)',
            },
          ],
        },
        J: {
          isTotalView: true,
          isNonBorrowerPaidFieldsIncluded: true,
          isFeeProviderColumnVisible: false,
          totalAmount: calculatedSections?.J?.totalAmount,
          items: [
            {
              ...(calculatedSections?.J || emptyCalculatedSectionTotals),
              name: 'Closing Costs Subtotals (D + I)',
            },
            {
              name:
                totalCure && !isCureHeld
                  ? `Lender Credit (Includes ${formatNumberToCurrencyString(
                      totalCure,
                      currencyOptions,
                    )} credits for increase in Closing Cost above legal limit)`
                  : 'Lender Credits',
              heldPricingPremiumAmount:
                isPricingPremiumHeld && heldPricingPremiumAmount > 0 ? heldPricingPremiumAmount : 0,
              totalAtClosingAmount: -lenderCredit,
              modifiedFields: getFormattedModifiedFields(modifiedFields),
            },
          ],
        },
      },
      summariesOfTransactions: {
        K: {
          name: isRefinanceFeesFlow ? 'Payoffs and Payments' : 'Due from Borrower at Closing',
          labelNameAmountSection: isRefinanceFeesFlow ? 'Payment To' : 'Name',
          totalAmount: sectionK.kTotalAmount,
          items: isRefinanceFeesFlow
            ? [
                ...(sectionK.paymentsPayoffs || []).map(
                  ({ description, amount, modifiedFields, ...rest }) => ({
                    name: description,
                    amount,
                    modifiedFields: getFormattedModifiedFields(modifiedFields),
                    ...rest,
                  }),
                ),
              ]
            : [
                {
                  name: 'Sale Price of Property',
                  amount: sectionK.salePriceOfProperty,
                  modifiedFields: getFormattedModifiedFields(sectionK.modifiedFields),
                },
                {
                  name: 'Closing Costs Paid At Closing Section J',
                  amount: sectionK.closingCostsPaidAtClosingSectionJ,
                  modifiedFields: getFormattedModifiedFields(sectionK.modifiedFields),
                },
                ...getConditionalArray(sectionK.adjustmentsPaidBySeller?.length, [
                  {
                    name: '-',
                  },
                ]),
                ...naturalSortBy(
                  sectionK.adjustmentsPaidBySeller || [],
                  'sellerAdjustmentName',
                ).map(
                  ({
                    sellerAdjustmentName: name,
                    totalAmount,
                    startDate,
                    endDate,
                    modifiedFields,
                    ...rest
                  }) => {
                    return {
                      name: getSellerAdjustmentViewLabel({ name, startDate, endDate }),
                      amount: totalAmount,
                      modifiedFields: getFormattedModifiedFields(modifiedFields),
                      ...rest,
                    };
                  },
                ),
              ],
        },
        ...getConditionalObject(!isRefinanceFeesFlow, () => ({
          L: {
            name: 'Paid already by or on Behalf of Borrower at Closing',
            totalAmount: sectionL.lTotalAmount,
            modifiedFields: getFormattedModifiedFields(sectionL.modifiedFields),
            items: [
              {
                name: 'Gifts Fund',
                amount: sectionL.giftsFund,
                modifiedFields: getFormattedModifiedFields(sectionL.modifiedFields),
              },
              {
                name: 'Total Loan Amount',
                amount: sectionL.loanAmount,
                modifiedFields: getFormattedModifiedFields(sectionL.modifiedFields),
              },
              {
                name: 'Seller Credit Amount',
                amount: sectionL.sellerCreditAmount,
                modifiedFields: getFormattedModifiedFields(sectionL.modifiedFields),
              },
              {
                name: 'Subordinate Mortgages Proceeds',
                amount: sectionL.subordinateMortgagesProceeds,
                modifiedFields: getFormattedModifiedFields(sectionL.modifiedFields),
              },
              {
                name: 'Grants',
                amount: sectionL.grants,
                modifiedFields: getFormattedModifiedFields(sectionL.modifiedFields),
              },
              ...(sectionL.otherCreditsSectionL || []).map(({ description, amount }) => {
                return {
                  name: description,
                  amount,
                  modifiedFields: getFormattedModifiedFields(sectionL.modifiedFields),
                };
              }),
              ...getConditionalArray(sectionL.adjustmentsUnPaidBySeller?.length, [
                {
                  name: '-',
                },
              ]),
              ...naturalSortBy(
                sectionL.adjustmentsUnPaidBySeller || [],
                'sellerAdjustmentName',
              ).map(
                ({
                  sellerAdjustmentName: name,
                  totalAmount,
                  startDate,
                  endDate,
                  modifiedFields,
                  ...rest
                }) => {
                  return {
                    name: getSellerAdjustmentViewLabel({ name, startDate, endDate }),
                    amount: totalAmount,
                    modifiedFields: getFormattedModifiedFields(modifiedFields),
                    ...rest,
                  };
                },
              ),
            ],
          },
        })),
      },
      calculation: [
        ...getConditionalArray(isRefinanceFeesFlow, () => [
          {
            name: 'Total Amount',
            amount: totalLoanAmount,
            modifiedFields: getFormattedModifiedFields(modifiedFields),
          },
          {
            name: 'Total Closing Costs (J)',
            amount: sectionK.closingCostsPaidAtClosingSectionJ,
            modifiedFields: getFormattedModifiedFields(sectionK.modifiedFields),
          },
          {
            name: 'Closing Costs Paid Before Closing',
            amount: closingCostsPayedBeforeClosing,
            modifiedFields: getFormattedModifiedFields(modifiedFields),
          },
          {
            name: 'Total Payoffs and Payments (K)',
            amount: -sectionK.paymentsPayoffs?.reduce((acc, p) => acc + p.amount, 0),
            modifiedFields: [
              _.some(sectionK.paymentsPayoffs, ({ modifiedFields }) =>
                _.includes(modifiedFields, 'amount'),
              )
                ? 'amount'
                : '',
            ],
          },
        ]),
        ...getConditionalArray(!isRefinanceFeesFlow, () => [
          {
            name: 'Total Due from Borrower at Closing (K)',
            amount: sectionK.kTotalAmount,
            modifiedFields: getFormattedModifiedFields(sectionK.modifiedFields),
          },
          {
            name: 'Total Paid Already by or on Behalf of Borrower at Closing (L)',
            amount: -sectionL.lTotalAmount,
            modifiedFields: getFormattedModifiedFields(sectionL.modifiedFields),
          },
        ]),
        {
          name: (
            <Typography color="text.primary" variant="bold14">
              CASH TO CLOSE
            </Typography>
          ),
          amount: cashToClose?.cashToCloseAmount,
          modifiedFields: getFormattedModifiedFields(cashToClose?.modifiedFields),
        },
      ],
    };
  },
);

export const selectEditableFeesSections = createSelector(
  selectIsRefinanceFeesFlow,
  isRefinanceFeesFlow => (isRefinanceFeesFlow ? editableSections : [...editableSections, 'K', 'L']),
);

export const selectImpoundsFeesView = createSelector(selectLoanFeesView, loanFeesView => {
  const { aggregateAdjustment, monthsCushion, sectionG = [], modifiedFields } = loanFeesView;
  const impounds = sectionG.map(impound => {
    const { modifiedFields: impModifiedFields } = impound;
    const { monthlyAmount, collectedAmount } = getCalculatedImpoundFields(impound);

    const formattedModFields = (impModifiedFields || []).map(fieldStr => {
      // looks like "dueDate [0]" or "amountDue [0]"
      if (fieldStr.includes('amountDue') || fieldStr.includes('dueDate')) {
        const [fieldName, index] = fieldStr.split(' ');

        return `amountDueDates${index}.${fieldName}`;
      }

      return fieldStr;
    });

    return { ...impound, monthlyAmount, collectedAmount, modifiedFields: formattedModFields };
  });

  return {
    aggregateAdjustment,
    monthsCushion,
    modifiedFields: modifiedFields || [],
    impounds: naturalSortBy(impounds, 'impoundTypeName'),
  };
});

export const selectLoanFeesSectionKEdit = createSelector(
  [selectLoanFeesView, selectIsRefinanceFeesFlow, selectSellerAdjustmentTypesOptions],
  (loanFeesView, isRefinanceFeesFlow, sellerAdjustmentTypesOptions) => {
    const sectionK = loanFeesView.sectionK ?? {};
    const sectionL = loanFeesView.sectionL ?? {};

    const modifiedFieldsSectionK = getFormattedModifiedFields(sectionK.modifiedFields);

    return !isRefinanceFeesFlow
      ? {
          tableTitle: 'Adjustments for Items Paid by Seller in Advance',

          adjustmentTypeOptions: sellerAdjustmentTypesOptions ?? [],

          adjustmentTypeOptionsNotDeleted: sellerAdjustmentTypesOptions.filter(
            ({ isDeleted }) => !isDeleted,
          ),

          modifiedFields: modifiedFieldsSectionK,

          dataDisplayItems: [
            {
              id: 'salePriceOfProperty',
              name: 'Sale Price of Property',
              amount: formatNumberToCurrencyString(
                sectionK.salePriceOfProperty ?? 0,
                currencyOptions,
              ),
            },

            {
              id: 'closingCostsPaidAtClosingSectionJ',
              name: 'Closing Costs Paid At Closing Section J',
              amount: formatNumberToCurrencyString(
                sectionK.closingCostsPaidAtClosingSectionJ ?? 0,
                currencyOptions,
              ),
            },
          ],

          constantTotalAmount:
            sectionK.kTotalAmount -
            _.sumBy(sectionK.adjustmentsPaidBySeller, adjustment =>
              adjustment?.pendingChangesActionType !== PendingChangesActionType.DELETED
                ? Number(adjustment.totalAmount || 0)
                : 0,
            ),

          adjustmentsPaidBySeller: [
            ...naturalSortBy(sectionK.adjustmentsPaidBySeller || [], 'sellerAdjustmentName').map(
              ({ modifiedFields, applicationSellerAdjustmentId, ...rest }) => {
                return {
                  ...rest,
                  applicationSellerAdjustmentId,
                  adjustmentId: applicationSellerAdjustmentId,
                  modifiedFields: getFormattedModifiedFields(modifiedFields),
                };
              },
            ),
          ],

          adjustmentsUnpaidBySeller: [
            ...naturalSortBy(sectionL.adjustmentsUnPaidBySeller || [], 'sellerAdjustmentName').map(
              ({ modifiedFields, ...rest }) => {
                return {
                  ...rest,
                  modifiedFields: getFormattedModifiedFields(modifiedFields),
                };
              },
            ),
          ],
        }
      : null;
  },
);

export const selectLoanFeesSectionLEdit = createSelector(
  [selectLoanFeesView, selectIsRefinanceFeesFlow, selectSellerAdjustmentTypesOptions],
  (loanFeesView, isRefinanceFeesFlow, sellerAdjustmentTypesOptions) => {
    const sectionK = loanFeesView.sectionK ?? {};
    const sectionL = loanFeesView.sectionL ?? {};

    const modifiedFieldsSectionL = getFormattedModifiedFields(sectionL.modifiedFields);

    return !isRefinanceFeesFlow
      ? {
          tableTitle: 'Adjustments for Items Unpaid by Seller',

          adjustmentTypeOptions: sellerAdjustmentTypesOptions ?? [],

          adjustmentTypeOptionsNotDeleted: sellerAdjustmentTypesOptions.filter(
            ({ isDeleted }) => !isDeleted,
          ),

          modifiedFields: modifiedFieldsSectionL,

          dataDisplayItems: [
            {
              id: 'deposit',
              name: 'Deposit',
              amount: formatNumberToCurrencyString(sectionL.deposit ?? 0, currencyOptions),
            },

            {
              id: 'loanAmount',
              name: 'Loan Amount',
              amount: formatNumberToCurrencyString(sectionL.loanAmount ?? 0, currencyOptions),
            },

            {
              id: 'sellerCreditAmount',
              name: 'Seller Credit',
              amount: formatNumberToCurrencyString(
                sectionL.sellerCreditAmount ?? 0,
                currencyOptions,
              ),
            },
          ],

          constantTotalAmount:
            sectionL.lTotalAmount -
            _.sumBy(sectionL.adjustmentsUnPaidBySeller, adjustment =>
              adjustment?.pendingChangesActionType !== PendingChangesActionType.DELETED
                ? Number(adjustment.totalAmount || 0)
                : 0,
            ),

          otherCredits: naturalSortBy(sectionL.otherCreditsSectionL ?? [], 'description').map(
            ({ description, amount }) => ({
              id: crypto.randomUUID(),
              name: description,
              amount: formatNumberToCurrencyString(amount ?? 0, currencyOptions),
            }),
          ),

          adjustmentsUnpaidBySeller: [
            ...naturalSortBy(sectionL.adjustmentsUnPaidBySeller || [], 'sellerAdjustmentName').map(
              ({ modifiedFields, applicationSellerAdjustmentId, ...rest }) => {
                return {
                  ...rest,
                  applicationSellerAdjustmentId,
                  adjustmentId: applicationSellerAdjustmentId,
                  modifiedFields: getFormattedModifiedFields(modifiedFields),
                };
              },
            ),
          ],

          adjustmentsPaidBySeller: [
            ...naturalSortBy(sectionK.adjustmentsPaidBySeller || [], 'sellerAdjustmentName').map(
              ({ modifiedFields, ...rest }) => {
                return {
                  ...rest,
                  modifiedFields: getFormattedModifiedFields(modifiedFields),
                };
              },
            ),
          ],
        }
      : null;
  },
);
